import type { ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { NotificationType } from '@hunting-nature/shared-core/enums/push-notification-event-type.enum';
import type {
  NotificationCameraData,
  NotificationData,
  NotificationExternalUrlData,
  NotificationImageData,
  ParsedPushNotificationPayload,
} from '@hunting-nature/shared-core/interfaces/push-notification-payload.interface';

export function parsePushNotificationPayload(notification: PushNotificationSchema): ParsedPushNotificationPayload | null {
  return parseNotificationPayload(notification, {
    coldstart: false,
    foreground: true,
    userInteraction: false,
  });
}

export function parsePushActionPayload(action: ActionPerformed): ParsedPushNotificationPayload | null {
  return parseNotificationPayload(action.notification, {
    coldstart: false,
    foreground: false,
    userInteraction: action.actionId === 'tap',
  });
}

function parseNotificationPayload(
  notification: PushNotificationSchema,
  defaults: Pick<ParsedPushNotificationPayload, 'coldstart' | 'foreground' | 'userInteraction'>
): ParsedPushNotificationPayload | null {
  const payload = asObject(notification.data);
  if (!payload) {
    return null;
  }

  const notificationRecord = asObject(notification);
  const parsedPayload: ParsedPushNotificationPayload = {
    coldstart: readBoolean(notificationRecord, 'coldstart') ?? readBoolean(payload, 'coldstart') ?? defaults.coldstart,
    foreground: readBoolean(notificationRecord, 'foreground') ?? readBoolean(payload, 'foreground') ?? defaults.foreground,
    userInteraction: readBoolean(notificationRecord, 'userInteraction') ?? readBoolean(payload, 'userInteraction') ?? defaults.userInteraction,
    notification: {
      title: readNotificationText(notification.title, payload, 'title'),
      body: readNotificationText(notification.body, payload, 'body'),
    },
  };

  const data = parseNotificationData(customFieldsOf(payload));
  if (data) {
    parsedPayload.data = data;
  }

  return parsedPayload;
}

/**
 * Locates the app's own fields inside what the plugin hands over.
 *
 * The two platforms deliver different shapes for the same hub template. Android passes the
 * FCM `data` map as-is, so the fields are top-level. iOS passes the entire APNs `userInfo`,
 * where the backend's template puts them under a `data` key next to `aps` — one level down.
 * Verified against a captured production-template push on 2026-09-13; before this the iOS
 * payload parsed without a `data` block and a tap on the banner navigated nowhere.
 */
function customFieldsOf(payload: Record<string, unknown>): Record<string, unknown> {
  if (readNotificationType(payload)) {
    return payload;
  }

  const nested = asObject(payload['data']);
  return nested && readNotificationType(nested) ? nested : payload;
}

function asObject(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function readString(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readBoolean(payload: Record<string, unknown> | null, key: string): boolean | null {
  if (!payload) {
    return null;
  }

  const value = payload[key];
  return typeof value === 'boolean' ? value : null;
}

function readNotificationText(topLevelValue: string | undefined, payload: Record<string, unknown>, key: 'title' | 'body'): string {
  if (typeof topLevelValue === 'string') {
    return topLevelValue;
  }

  const nestedNotification = asObject(payload['notification']);
  return readString(nestedNotification ?? payload, key) ?? '';
}

function readNotificationType(payload: Record<string, unknown>): NotificationType | null {
  const value = readString(payload, 'notificationType') ?? readString(payload, 'eventType');
  if (!value) {
    return null;
  }

  return Object.values(NotificationType).includes(value as NotificationType) ? (value as NotificationType) : null;
}

function parseNotificationData(payload: Record<string, unknown>): NotificationData | null {
  const notificationType = readNotificationType(payload);
  if (!notificationType) {
    return null;
  }

  switch (notificationType) {
    case NotificationType.MEDIA_NEW_IMAGE:
      return parseImageData(payload, notificationType);
    case NotificationType.CAMERA_STATUS:
      return parseCameraData(payload, notificationType);
    case NotificationType.MARKETING_EXTERNAL_URL:
      return parseExternalUrlData(payload, notificationType);
    default:
      return null;
  }
}

function parseImageData(payload: Record<string, unknown>, notificationType: NotificationType.MEDIA_NEW_IMAGE): NotificationImageData | null {
  const id = readString(payload, 'id') ?? readString(payload, 'imageId');
  const imageUrl = readString(payload, 'imageUrl');
  const imei = readString(payload, 'imei');

  if (!id || !imageUrl || !imei) {
    return null;
  }

  return {
    id,
    imageUrl,
    imei,
    notificationType,
  };
}

function parseCameraData(payload: Record<string, unknown>, notificationType: NotificationType.CAMERA_STATUS): NotificationCameraData | null {
  const camera_id = readString(payload, 'camera_id') ?? readString(payload, 'cameraId');
  const imei = readString(payload, 'imei');

  if (!camera_id || !imei) {
    return null;
  }

  return {
    camera_id,
    imei,
    notificationType,
  };
}

function parseExternalUrlData(payload: Record<string, unknown>, notificationType: NotificationType.MARKETING_EXTERNAL_URL): NotificationExternalUrlData | null {
  const targetUrl = readString(payload, 'targetUrl');
  if (!targetUrl) {
    return null;
  }

  return {
    targetUrl,
    notificationType,
  };
}
